import { ref, onMounted, onUnmounted, watch } from 'vue'

/**
 * Composable for inactivity timer
 * @param {number} timeoutSeconds - Timeout in seconds
 * @param {Function} onTimeout - Callback when timeout is reached
 */
export function useInactivityTimer(timeoutSeconds = 60, onTimeout) {
  const remainingSeconds = ref(timeoutSeconds)
  const isActive = ref(true)
  let intervalId = null
  let resetTimeoutId = null

  const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click']

  function resetTimer() {
    remainingSeconds.value = timeoutSeconds

    // Debounce reset to avoid excessive updates
    if (resetTimeoutId) {
      clearTimeout(resetTimeoutId)
    }
    resetTimeoutId = setTimeout(() => {
      resetTimeoutId = null
    }, 100)
  }

  function startTimer() {
    if (intervalId) return

    isActive.value = true
    remainingSeconds.value = timeoutSeconds

    intervalId = setInterval(() => {
      if (remainingSeconds.value > 0) {
        remainingSeconds.value--
      } else {
        stopTimer()
        if (onTimeout) {
          onTimeout()
        }
      }
    }, 1000)

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true })
    })
  }

  function stopTimer() {
    isActive.value = false

    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }

    // Remove event listeners
    events.forEach(event => {
      document.removeEventListener(event, resetTimer)
    })
  }

  function pauseTimer() {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  function resumeTimer() {
    if (!isActive.value) return

    if (!intervalId) {
      intervalId = setInterval(() => {
        if (remainingSeconds.value > 0) {
          remainingSeconds.value--
        } else {
          stopTimer()
          if (onTimeout) {
            onTimeout()
          }
        }
      }, 1000)
    }
  }

  // Watch for timeout changes
  watch(() => timeoutSeconds, (newTimeout) => {
    if (newTimeout > 0 && remainingSeconds.value > newTimeout) {
      remainingSeconds.value = newTimeout
    }
  })

  onMounted(() => {
    startTimer()
  })

  onUnmounted(() => {
    stopTimer()
  })

  return {
    remainingSeconds,
    isActive,
    resetTimer,
    startTimer,
    stopTimer,
    pauseTimer,
    resumeTimer
  }
}

export default useInactivityTimer
